// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Center, Stack, Text, Title } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkLoadError: boolean;
}

/**
 * Error boundary that handles chunk load failures gracefully.
 *
 * When a new deployment happens, old JavaScript chunks are replaced with
 * new ones (with different hashes). Users with cached HTML may try to
 * load old chunks that no longer exist, causing "Failed to fetch dynamically
 * imported module" errors.
 *
 * This component catches those errors and prompts the user to reload,
 * which will fetch the new HTML and chunk manifest.
 */
export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkLoadError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a chunk load error
    const isChunkLoadError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.name === 'ChunkLoadError';

    return { hasError: true, isChunkLoadError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ChunkLoadErrorBoundary] Caught error:', error, errorInfo);

    // If it's a chunk load error, we could auto-reload after a short delay
    // But it's better UX to let the user know what's happening
    if (this.state.isChunkLoadError) {
      console.log('[ChunkLoadErrorBoundary] Chunk load error detected - app may have been updated');
    }
  }

  handleReload = (): void => {
    // Force a full page reload to get the new chunks
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const t = i18n.t.bind(i18n);

      if (this.state.isChunkLoadError) {
        return (
          <Center style={{ height: '100vh' }}>
            <Stack align="center" gap="md">
              <IconRefresh size={48} color="var(--mantine-color-blue-6)" />
              <Title order={3}>{t('errors.applicationUpdated')}</Title>
              <Text c="dimmed" ta="center" maw={400}>
                {t('errors.newVersionDeployed')}
              </Text>
              <Button
                leftSection={<IconRefresh size={16} />}
                onClick={this.handleReload}
              >
                {t('errors.reloadApplication')}
              </Button>
            </Stack>
          </Center>
        );
      }

      // For other errors, show a generic error message
      return (
        <Center style={{ height: '100vh' }}>
          <Stack align="center" gap="md">
            <Title order={3}>{t('errors.somethingWentWrong')}</Title>
            <Text c="dimmed" ta="center" maw={400}>
              {t('errors.unexpectedError')}
            </Text>
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={this.handleReload}
            >
              {t('errors.reloadPage')}
            </Button>
          </Stack>
        </Center>
      );
    }

    return this.props.children;
  }
}
